import {
    type FC,
    type ReactNode,
    useEffect,
    useState,
} from "react";
import { useShopfrontDispatch, useShopfrontSelector } from "../../../_Bootstrap/hooks";
import { startLoading, stopLoading } from "../../../Actions/General/LayoutActions";
import { updateQuickMenu } from "../../../Actions/Users/UserActions";
import PopupManager from "../../../Common/Popups/PopupManager";
import { determineDataSource } from "../../../Data/DataStores/DataSource";
import { Permissions } from "../../../Utilities/Domain/Users/Permissions";
import { getOption, useOption } from "../../../Utilities/Platform/Options/Options";
import { Subscription } from "../../../Utilities/Platform/Subscription/Subscription";
import DisplayAgent from "../../../Workers/CustomerDisplay/DisplayAgent";
import { NAVIGATION_USER_UPDATE } from "../../Navigation/Navigation";
import { NavigationGroup } from "../../Navigation/NavigationGroup";
import { NavigationItem } from "../../Navigation/NavigationItem";
import { NavigationList } from "../../Navigation/NavigationList";

export const ShopfrontGeneralMenu: FC = () => {
    const dispatch = useShopfrontDispatch();
    const user = useShopfrontSelector(state => state.user.id);
    const [ userPermissions, setUserPermissions ] = useState<Record<string, number> | null>({});
    const customerDisplayOpen = useShopfrontSelector(state => state.customerDisplay.visible > 0);
    const hostFileVisible = useOption("flag:view_host_file");

    useEffect(() => {
        const loadUser = async () => {
            if(user === false) {
                return;
            }

            const source = await determineDataSource();
            const localUser = await source.users.get(user);

            dispatch(updateQuickMenu(localUser.quick_menu));
            setUserPermissions(localUser.permissions);
        };

        void loadUser();

        document.addEventListener(NAVIGATION_USER_UPDATE, loadUser, false);

        return () => {
            document.removeEventListener(NAVIGATION_USER_UPDATE, loadUser);
        };
    }, [ dispatch, user ]);

    const determineUserCan = <
        T extends ReadonlyArray<string>,
        TV extends T[number],
        TR extends Record<TV, boolean>
    >(permissions: T): false | TR => {
        const map = {} as Record<string, boolean>;

        if(userPermissions === null) {
            for(let i = 0, l = permissions.length; i < l; i++) {
                map[permissions[i]] = true;
            }

            return map as TR;
        }

        if(Array.isArray(userPermissions)) {
            return false;
        }

        for(let i = 0, l = permissions.length; i < l; i++) {
            map[permissions[i]] = Permissions.checkPermissions(
                [ permissions[i] ],
                userPermissions
            );
        }

        if(Object.values(map).every((p) => !p)) {
            return false;
        }

        return map as TR;
    };

    const renderRegister = (): ReactNode => {
        let customerDisplay = "Open Customer Display";

        if(customerDisplayOpen) {
            customerDisplay = "Close Customer Display";
        }

        const requiredPermissions = [
            "manage_takings_adjust_movement",
            "see_manage_takings",
            "see_history",
            "use_bump_screen",
        ] as const;

        let permissions = determineUserCan(requiredPermissions);

        if(!permissions) {
            permissions = {
                manage_takings_adjust_movement: false,
                see_manage_takings            : false,
                see_history                   : false,
                use_bump_screen               : false,
            };
        }

        return (
            <NavigationGroup
                icon="shopping-cart"
                label="Register"
            >
                {permissions.manage_takings_adjust_movement && (
                    <NavigationItem
                        icon="money-bill"
                        to="/manage-cash"
                    >
                        Manage Cash
                    </NavigationItem>
                )}
                {permissions.see_manage_takings && (
                    <NavigationItem
                        icon="university"
                        to="/close"
                    >
                        Close Register
                    </NavigationItem>
                )}
                {permissions.see_history && (
                    <NavigationItem
                        icon="book"
                        to="/history"
                    >
                        Sales History
                    </NavigationItem>
                )}
                {permissions.use_bump_screen && Subscription.can(Subscription.Features.KITCHEN_DISPLAY) && (
                    <NavigationItem
                        icon="hat-chef"
                        to="/kitchen/bump"
                    >
                        Kitchen Display
                    </NavigationItem>
                )}
                <NavigationItem
                    icon="tv"
                    to={(e) => {
                        if(customerDisplayOpen) {
                            void DisplayAgent.getInstance().closeDisplay();
                        } else {
                            e.stopPropagation();

                            dispatch(startLoading());

                            determineDataSource()
                                .then(source => {
                                    return source.customerDisplays.get(getOption("register_customer_display"));
                                })
                                .then(() => {
                                    dispatch(stopLoading());

                                    return DisplayAgent.getInstance().openDisplay();
                                })
                                .catch(err => {
                                    dispatch(stopLoading());

                                    console.error("Error loading customer display", err);
                                    PopupManager.error(
                                        "Failed to open customer display. Please check that a customer display " +
                                        "template is selected for this register."
                                    );
                                });
                        }
                    }}
                >
                    {customerDisplay}
                </NavigationItem>
            </NavigationGroup>
        );
    };

    const renderStockManagement = (): ReactNode => {
        const requiredPermissions = [
            "see_products",
            "see_classifications",
            "see_suppliers",
            "see_stock_management",
            "see_stocktake",
            "edit_products",
            "see_product_backorders",
        ] as const;

        const permissions = determineUserCan(requiredPermissions);

        if(!permissions) {
            return null;
        }

        const f = false;

        return (
            <NavigationGroup
                icon="barcode-alt"
                label="Stock Management"
            >
                {permissions.see_products && (
                    <NavigationItem
                        icon="glass-martini"
                        to="/products"
                    >
                        Products
                    </NavigationItem>
                )}
                {permissions.see_classifications && (
                    <NavigationItem
                        icon="tags"
                        to="/classifications"
                    >
                        Classifications
                    </NavigationItem>
                )}
                {permissions.see_suppliers && (
                    <NavigationItem
                        icon="truck"
                        to="/suppliers"
                    >
                        Suppliers
                    </NavigationItem>
                )}
                {permissions.see_stock_management && (
                    <NavigationItem
                        icon="cubes"
                        to="/orders"
                    >
                        Orders &amp; Invoices
                    </NavigationItem>
                )}
                {permissions.see_stocktake && (
                    <NavigationItem
                        icon="list-ol"
                        to="/stocktake"
                    >
                        Stocktake
                    </NavigationItem>
                )}
                {f && permissions.see_product_backorders && (
                    <NavigationItem
                        icon="clipboard-list-check"
                        to="/backorders"
                    >
                        Product Backorders
                    </NavigationItem>
                )}
                {permissions.see_products && (
                    <NavigationItem
                        icon="plus"
                        to="/stock/more"
                    >
                        More
                    </NavigationItem>
                )}
            </NavigationGroup>
        );
    };

    const renderCustomerManagement = (): ReactNode => {
        const requiredPermissions = [
            "see_customers",
            "see_customer_groups",
            "see_pricelists",
            "see_balances",
            "manage_gift_cards",
        ] as const;

        const permissions = determineUserCan(requiredPermissions);

        if(!permissions) {
            return null;
        }

        return (
            <NavigationGroup
                icon="address-book"
                label="Customer Management"
            >
                {permissions.see_customers && (
                    <NavigationItem
                        icon="address-card"
                        to="/customers"
                    >
                        Customers
                    </NavigationItem>
                )}
                {permissions.see_customer_groups && (
                    <NavigationItem
                        icon="object-group"
                        to="/customer-groups"
                    >
                        Customer Groups
                    </NavigationItem>
                )}
                {permissions.see_pricelists && (
                    <NavigationItem
                        icon="dollar-sign"
                        to="/pricelists"
                    >
                        Price Lists
                    </NavigationItem>
                )}
                {permissions.see_balances && (
                    <NavigationItem
                        icon="balance-scale"
                        to="/customers/balances"
                    >
                        Balances
                    </NavigationItem>
                )}
                {permissions.manage_gift_cards && (
                    <NavigationItem
                        icon="gift"
                        to="/giftcards"
                    >
                        Gift Cards
                    </NavigationItem>
                )}
            </NavigationGroup>
        );
    };

    const renderMarketing = (): ReactNode => {
        const requiredPermissions = [
            "see_promotions",
            "see_shelf_tickets",
            "see_media_centre",
        ] as const;

        const permissions = determineUserCan(requiredPermissions);

        if(!permissions) {
            return null;
        }

        return (
            <NavigationGroup
                icon="bullhorn"
                label="Marketing"
            >
                {permissions && permissions.see_promotions && (
                    <NavigationItem
                        icon="hand-point-down"
                        to="/promotions"
                    >
                        Promotions
                    </NavigationItem>
                )}
                {permissions && permissions.see_shelf_tickets && (
                    <NavigationItem
                        icon="ticket"
                        to="/tickets"
                    >
                        Shelf Tickets
                    </NavigationItem>
                )}
                {permissions && permissions.see_media_centre && (
                    <NavigationItem
                        icon="tv-retro"
                        to="/media"
                    >
                        Media Centre
                    </NavigationItem>
                )}
            </NavigationGroup>
        );
    };

    const renderReporting = (): ReactNode => {
        const requiredPermissions = [
            "view_reporting_dashboard",
            "view_register_closures",
            "run_actions_reports",
            "run_advanced_reports",
            "run_inventory_reports",
            "run_purchase_reports",
            "run_sales_reports",
            "run_misc_reports",
            "see_recent_register_closures",
            "see_security_events",
        ] as const;

        const permissions = determineUserCan(requiredPermissions);

        if(!permissions) {
            return null;
        }

        const canViewFavourites = (
            permissions.run_actions_reports ||
            permissions.run_advanced_reports ||
            permissions.run_inventory_reports ||
            permissions.run_purchase_reports ||
            permissions.run_sales_reports
        );

        return (
            <NavigationGroup
                icon="chart-area"
                label="Reporting"
            >
                {permissions.view_reporting_dashboard && (
                    <NavigationItem
                        icon="chart-pie"
                        to="/reports/dashboard"
                    >
                        Reporting Dashboard
                    </NavigationItem>
                )}
                {permissions.see_security_events && (
                    <NavigationItem
                        icon="shield"
                        to="/security"
                    >
                        Security Centre
                    </NavigationItem>
                )}
                {permissions.run_sales_reports && (
                    <NavigationItem
                        icon="chart-line"
                        to="/reports/sales"
                    >
                        Sales Reports
                    </NavigationItem>
                )}
                {permissions.run_inventory_reports && (
                    <NavigationItem
                        icon="archive"
                        to="/reports/inventory"
                    >
                        Inventory Reports
                    </NavigationItem>
                )}
                {permissions.run_purchase_reports && (
                    <>
                        <NavigationItem
                            icon="chart-bar"
                            to="/reports/purchase"
                        >
                            Purchase Reports
                        </NavigationItem>
                        <NavigationItem
                            icon="chart-scatter"
                            to="/reports/transfer"
                        >
                            Transfer Reports
                        </NavigationItem>
                    </>
                )}
                {permissions.run_actions_reports && (
                    <NavigationItem
                        icon="exclamation"
                        to="/reports/actions"
                    >
                        Actions Reports
                    </NavigationItem>
                )}
                {permissions.run_advanced_reports && (
                    <NavigationItem
                        icon="code"
                        to="/reports/advanced"
                    >
                        Advanced Reports
                    </NavigationItem>
                )}
                {canViewFavourites && (
                    <NavigationItem
                        icon="star"
                        to="/reports/favourites"
                    >
                        Favourite Reports
                    </NavigationItem>
                )}
                {permissions.run_misc_reports && (
                    <NavigationItem
                        icon="chart-network"
                        to="/reports/misc"
                    >
                        Miscellaneous Reports
                    </NavigationItem>
                )}
                {(permissions.view_register_closures || permissions.see_recent_register_closures) && (
                    <NavigationItem
                        icon="door-closed"
                        to="/reports/closures"
                    >
                        Register Closures
                    </NavigationItem>
                )}
            </NavigationGroup>
        );
    };

    const renderSettings = (): ReactNode => {
        const requiredPermissions = [
            "modify_settings",
            "manage_billing",
            "modify_sale_keys",
            "modify_receipts",
            "modify_statements",
            "modify_payment_methods",
            "modify_tax_rates",
            "modify_transfer_list",
            "modify_customer_displays",
            "create_users",
            "modify_users",
            "modify_roles_permissions",
            "modify_loyalty",
            "modify_hardware",
            "edit_products",
            "modify_integrations",
            "manage_push_devices",
            "modify_promotion_categories",
            "see_promotions",
            "create_products",
            "modify_registers_outlets",
            "modify_page_rules",
            "modify_promotion_categories",
            "modify_barcode_templates",
            "view_enterprise_connections",
            "modify_surcharges",
        ] as const;

        const permissions = determineUserCan(requiredPermissions);

        if(!permissions) {
            return null;
        }

        return (
            <NavigationGroup
                icon="wrench"
                label="Setup"
            >
                {permissions.modify_settings && (
                    <NavigationItem
                        icon="cogs"
                        to="/settings"
                    >
                        General
                    </NavigationItem>
                )}
                {permissions.manage_billing && (
                    <NavigationItem
                        icon="exchange"
                        to="/settings/account"
                    >
                        Account &amp; Billing
                    </NavigationItem>
                )}
                {permissions.modify_sale_keys && (
                    <NavigationItem
                        icon="keyboard"
                        to="/settings/keys"
                    >
                        Sale Keys
                    </NavigationItem>
                )}
                {permissions.modify_receipts && (
                    <NavigationItem
                        icon="receipt"
                        to="/settings/receipts"
                    >
                        Receipts
                    </NavigationItem>
                )}
                {permissions.modify_statements && (
                    <NavigationItem
                        icon="envelope"
                        to="/settings/statements"
                    >
                        Statements
                    </NavigationItem>
                )}
                {permissions.modify_payment_methods && (
                    <NavigationItem
                        icon="credit-card-blank"
                        to="/settings/payment"
                    >
                        Payment Methods
                    </NavigationItem>
                )}
                {permissions.modify_tax_rates && (
                    <NavigationItem
                        icon="percent"
                        to="/settings/tax"
                    >
                        Tax Rates
                    </NavigationItem>
                )}
                {permissions.modify_transfer_list && (
                    <NavigationItem
                        icon="repeat"
                        to="/settings/transferees"
                    >
                        Transfer List
                    </NavigationItem>
                )}
                {permissions.modify_customer_displays && (
                    <NavigationItem
                        icon="desktop"
                        to="/settings/customerdisplay"
                    >
                        Customer Display
                    </NavigationItem>
                )}
                {(permissions.modify_users || permissions.create_users) && (
                    <NavigationItem
                        icon="users"
                        to="/settings/users"
                    >
                        Users
                    </NavigationItem>
                )}
                {permissions.modify_roles_permissions && (
                    <NavigationItem
                        icon="unlock"
                        to="/settings/roles"
                    >
                        Roles &amp; Permissions
                    </NavigationItem>
                )}
                {permissions.modify_loyalty && (
                    <NavigationItem
                        icon="trophy"
                        to="/settings/loyalty"
                    >
                        Loyalty
                    </NavigationItem>
                )}
                {permissions.modify_surcharges && (
                    <NavigationItem
                        icon="money-check-edit"
                        to="/settings/surcharging"
                    >
                        Surcharging
                    </NavigationItem>
                )}
                {permissions.modify_hardware && (
                    <NavigationItem
                        icon="print"
                        to="/settings/hardware"
                    >
                        Hardware
                    </NavigationItem>
                )}
                {permissions.edit_products && permissions.modify_settings && permissions.create_products && (
                    <NavigationItem
                        icon="info"
                        to="/settings/additional-information"
                    >
                        Additional Information
                    </NavigationItem>
                )}
                {permissions.modify_page_rules && (
                    <NavigationItem
                        icon="random"
                        to="/settings/rules"
                    >
                        Page Rules
                    </NavigationItem>
                )}
                {permissions.modify_integrations && (
                    <NavigationItem
                        icon="puzzle-piece"
                        to="/settings/integrations"
                    >
                        Integrations
                    </NavigationItem>
                )}
                {permissions.manage_push_devices && (
                    <NavigationItem
                        icon="mobile"
                        to="/settings/push"
                    >
                        Push Notifications
                    </NavigationItem>
                )}
                {permissions.modify_promotion_categories && (
                    <NavigationItem
                        icon="object-ungroup"
                        to="/promotion/categories"
                    >
                        Promotion Categories
                    </NavigationItem>
                )}
                {permissions.modify_barcode_templates && (
                    <NavigationItem
                        icon="barcode-scan"
                        to="/settings/barcodes"
                    >
                        Barcode Templates
                    </NavigationItem>
                )}
                {permissions.modify_settings && permissions.edit_products && permissions.create_products && (
                    <NavigationItem
                        icon="sliders-h"
                        to="/settings/pricesets"
                    >
                        Price Sets
                    </NavigationItem>
                )}
                {permissions.modify_registers_outlets && (
                    <NavigationItem
                        icon="map-marker"
                        to="/settings/registers-outlets"
                    >
                        Registers &amp; Outlets
                    </NavigationItem>
                )}
                {permissions.view_enterprise_connections && (
                    <NavigationItem
                        icon="sitemap"
                        to="/enterprise"
                    >
                        Enterprise Policies
                    </NavigationItem>
                )}
            </NavigationGroup>
        );
    };

    const renderUtilities = (): ReactNode => {
        const requiredPermissions = [
            "see_trashed_items",
            "see_mail_log",
            "edit_products",
            "manage_vendor_connections",
            "see_host_connections",
            "edit_customers",
        ];

        const permissions = determineUserCan(requiredPermissions);

        if(!permissions) {
            return null;
        }

        return (
            <NavigationGroup
                icon="ambulance"
                label="Utilities"
            >
                {permissions.edit_products && (
                    <>
                        <NavigationItem
                            icon="calendar-edit"
                            to="/utilities/future-prices"
                        >
                            Future Prices
                        </NavigationItem>
                        <NavigationItem
                            icon="calendar-alt"
                            to="/utilities/future-costs"
                        >
                            Future Costs
                        </NavigationItem>
                    </>
                )}
                {permissions.see_mail_log && (
                    <NavigationItem
                        icon="mail-bulk"
                        to="/utilities/mail-log"
                    >
                        Mail Log
                    </NavigationItem>
                )}
                {
                    hostFileVisible &&
                    permissions.see_host_connections && (
                        <NavigationItem
                            icon="warehouse"
                            to="/utilities/host-connections"
                        >
                            Host Connections
                        </NavigationItem>
                    )
                }
                {permissions.manage_vendor_connections && (
                    <NavigationItem
                        icon="sort-alt"
                        to="/utilities/vendor-connections"
                    >
                        Vendor Connections
                    </NavigationItem>
                )}
                {permissions.see_trashed_items && (
                    <NavigationItem
                        icon="trash-alt"
                        to="/utilities/trashed-items"
                    >
                        Trashed Items
                    </NavigationItem>
                )}
                {permissions.edit_products && (
                    <NavigationItem
                        icon="barcode-alt"
                        to="/utilities/barcodes"
                    >
                        Barcodes
                    </NavigationItem>
                )}
                {permissions.edit_products && (
                    <NavigationItem
                        icon="box-full"
                        to="/utilities/products"
                    >
                        Product Utilities
                    </NavigationItem>
                )}
                {permissions.edit_customers && (
                    <NavigationItem
                        icon="users"
                        to="/utilities/customers"
                    >
                        Customer Utilities
                    </NavigationItem>
                )}
            </NavigationGroup>
        );
    };

    let canSell = userPermissions === null;

    if(!canSell) {
        if(!Array.isArray(userPermissions)) {
            canSell = Permissions.checkPermissions(
                [ "sell" ],
                userPermissions
            );
        }
    }

    return (
        <NavigationList>
            {canSell && (
                <NavigationItem
                    icon="shopping-bag"
                    to="/"
                >
                    Sell
                </NavigationItem>
            )}
            <NavigationItem
                icon="tachometer-alt"
                to="/dashboard"
            >
                Dashboard
            </NavigationItem>
            {renderRegister()}
            {renderStockManagement()}
            {renderCustomerManagement()}
            {renderMarketing()}
            {renderReporting()}
            {renderSettings()}
            {renderUtilities()}
            <NavigationItem
                icon="life-ring"
                to="https://support.onshopfront.com"
                external
            >
                Help
            </NavigationItem>
        </NavigationList>
    );
};
